import React, { useState, useRef } from 'react';
import {
  getScrDocumentUploadUrl,
  getScrDocumentDownloadUrl,
  deleteScrDocument,
  updateLifecycleProfile,
} from '../api/lifecycleApi';

const MAX_FILE_SIZE = 52_428_800; // 50MB

export default function ScrDocumentsPanel({
  toolId,
  lifecycleId,
  documents = [],
  onChange,
  callerEmail,
  projectId,
  readOnly = false,
}) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const fileInputRef = useRef(null);

  function clearMessages() {
    setError(null);
    setSuccess(null);
  }

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';

    clearMessages();

    if (file.size > MAX_FILE_SIZE) {
      setError('File exceeds 50MB limit');
      return;
    }

    setUploading(true);
    try {
      // 1. Get SAS upload URL
      const { upload_url, blob_name } = await getScrDocumentUploadUrl(
        toolId, lifecycleId, file.name, file.size, callerEmail
      );

      // 2. PUT file directly to blob
      const putRes = await fetch(upload_url, {
        method: 'PUT',
        headers: {
          'x-ms-blob-type': 'BlockBlob',
          'Content-Type': file.type || 'application/octet-stream',
        },
        body: file,
      });
      if (!putRes.ok) throw new Error('File upload to storage failed');

      // 3. Update LifecycleProfile with new document metadata
      const newDoc = {
        blob_name,
        original_filename: file.name,
        uploaded_by: callerEmail,
        uploaded_at: new Date().toISOString(),
        file_size_bytes: file.size,
      };
      const updatedDocs = [...documents, newDoc];
      await updateLifecycleProfile(toolId, { scr_documents: updatedDocs }, callerEmail, projectId);
      onChange(updatedDocs);
      setSuccess('Document uploaded');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  }

  async function handleDownload(blobName) {
    clearMessages();
    try {
      const { download_url } = await getScrDocumentDownloadUrl(blobName, callerEmail);
      window.open(download_url, '_blank');
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleDelete(blobName) {
    if (!window.confirm('Delete this document?')) return;
    clearMessages();
    try {
      const result = await deleteScrDocument(toolId, lifecycleId, blobName, callerEmail);
      onChange(result.scr_documents || []);
      setSuccess('Document deleted');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    }
  }

  function formatDate(iso) {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return iso;
    }
  }

  return (
    <div className="scr-docs-panel">
      <div className="scr-docs-header">
        <span className="ldp-label">Supporting Documents</span>
        {!readOnly && (
          <>
            <button
              className="scr-docs-upload-btn"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? 'Uploading...' : 'Upload File'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleUpload}
              style={{ display: 'none' }}
            />
          </>
        )}
      </div>

      {error && <div className="scr-docs-toast scr-docs-toast-error">{error}</div>}
      {success && <div className="scr-docs-toast scr-docs-toast-success">{success}</div>}

      {documents.length === 0 ? (
        <p className="scr-docs-empty">No documents uploaded.</p>
      ) : (
        <ul className="scr-docs-list">
          {documents.map((doc, idx) => (
            <li key={doc.blob_name || idx} className="scr-docs-item">
              <button
                className="scr-docs-filename"
                onClick={() => handleDownload(doc.blob_name)}
                title="Download"
              >
                {doc.original_filename || doc.blob_name}
              </button>
              <span className="scr-docs-meta">
                {doc.uploaded_by && `${doc.uploaded_by}`}
                {doc.uploaded_at && ` | ${formatDate(doc.uploaded_at)}`}
              </span>
              {!readOnly && (
                <button
                  className="scr-docs-delete"
                  onClick={() => handleDelete(doc.blob_name)}
                  title="Delete document"
                >
                  Delete
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
